import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useAuth } from "../../../../hooks/useAuth";
import { useParams } from "react-router-dom";
import {
  deletePreviewVideoImage,
  getTutorialById,
  getTutorialLevelList,
  postPreviewVideoImage,
  putEditTutorial,
} from "../../../../utils/api";
import { ErrorMessage, Field, Form, Formik } from "formik";
import rocketIcon from "../../../../assets/images/rocketIcon.svg";
import infoIcon from "../../../../assets/images/infoIcon.svg";
import primaryIcon from "../../../../assets/images/primaryIcon.svg";
import Fancybox from "../../../Projects/CreateProject/Fancybox";
import trashIcon from "../../../../assets/images/trashIcon.svg";
import loader from "../../../../assets/images/loader.gif";
import diskIcon from "../../../../assets/images/diskIcon.svg";
import Modal from "../../../../components/Modal";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteTutorialPreviewImage,
  fetchTutorialById,
  setTutorialPreviewImage,
} from "../../../../features/editTutorialSlice";
import cloudIcon from "../../../../assets/images/cloudIcon.svg";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import Tooltip from "../../../../components/Tooltip/Tooltip";

// Zustand Store
const useTutorialStore = create(
  devtools((set) => ({
    tutorialData: {},
    loading: false,
    error: null,
    fetchTutorialById_Zustand: async (accessToken, id) => {
      set({ loading: true, error: null });
      const response = await getTutorialById(accessToken, id);
      set({
        tutorialData: response.data.data,
        loading: false,
        error: false,
      });
      const error = !response.data.success;
      if (error) {
        set({
          loading: false,
          error,
        });
        throw new Error("Failed to fetch tutorial");
      }
    },
    setTutorialData: (tutorial) => set({ tutorialData: tutorial }),
  })),
);

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  tutorial_description: Yup.string()
    .min(8, "Description must be at least 15 characters")
    .required("Tutorial description is required"),
  link: Yup.string().url().required("Link to video is required"),
});

const EditTutorialForm = () => {
  const { accessToken } = useAuth();
  const dispatch = useDispatch();
  const { tutorialData, loading, error } = useSelector(
    (state) => state.editTutorial,
  );
  // const {
  //   tutorialData,
  //   setTutorialData,
  //   loading,
  //   error,
  //   fetchTutorialById_Zustand,
  // } = useTutorialStore();
  const [uploadingPrimaryImage, setUploadingPrimaryImage] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [levelList, setLevelList] = useState([]);
  const [previewImageName, setPreviewImageName] = useState("");
  const params = useParams();

  useEffect(() => {
    if (!accessToken) return;

    getTutorialLevelList(accessToken).then((res) => {
      if (res.data.success) {
        setLevelList(res.data.data);
      }
    });
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    dispatch(fetchTutorialById({ accessToken, id: params.id }));
    // fetchTutorialById_Zustand(accessToken, params.id);
  }, [
    dispatch,
    // fetchTutorialById_Zustand,
    params.id,
    accessToken,
  ]);

  const handleFileDrop = (event) => {
    event.preventDefault();
    // setFiles([...event.dataTransfer.files]);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];

    if (file) {
      setUploadingPrimaryImage(true);
      try {
        const response = await postPreviewVideoImage(accessToken, {
          file,
        });

        dispatch(setTutorialPreviewImage(response.data.data));
        // setTutorialData({ ...tutorialData, preview_url: response.data.data });
        // setPreviewImageName(response.data.data.preview_name);
      } catch (error) {
        console.error("Error uploading file:", error);
      } finally {
        setUploadingPrimaryImage(false);
      }
    }
  };

  const handleSubmit = (values, { setSubmitting }) => {
    putEditTutorial(
      accessToken,
      {
        ...values,
        preview_name: previewImageName || tutorialData.preview_name,
      },
      params.id,
    )
      .then((res) => {
        setIsError(false);
        setModalMessage("Tutorial was successfully updated!");
        setIsModalOpen(true);
      })
      .catch((err) => {
        setModalMessage(err.response.data.message);
        setIsError(true);
        setIsModalOpen(true);
      });

    setSubmitting(false);
  };

  const handleDeletePrimaryImage = () => {
    deletePreviewVideoImage(accessToken)
      .then((res) => {
        if (res.data.success) {
          dispatch(deleteTutorialPreviewImage());
          // setTutorialData({ ...tutorialData, preview_url: "" });
        }
      })
      .catch((err) => {
        setModalMessage(err.response?.data?.message);
        setIsError(true);
        setIsModalOpen(true);
      });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <Formik
      initialValues={{
        ...tutorialData,
        tutorial_description: tutorialData.description,
        difficulty_level: tutorialData.level,
      }}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleChange, handleSubmit, isSubmitting }) => (
        <Form className="bg-[#F7F7F7]" onSubmit={handleSubmit}>
          <div className="p-8">
            <div className="flex gap-[15px] lg:gap-0 flex-col lg:flex-row lg:items-center justify-between">
              <div>
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold">Edit Tutorial</h1>
                </div>
              </div>
            </div>

            <div className="flex mt-[35px] flex-col lg:flex-row">
              <div className="w-full lg:w-[70%]">
                <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] gap-[22px]">
                  <div className="flex items-center mb-[22px]">
                    <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                      <img
                        src={rocketIcon}
                        alt="Detail Icon"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <h2 className="text-[18px] text-black font-semibold flex items-center">
                        Tutorial Details
                      </h2>
                      <p className="text-[12px] text-[#8C8C8C]">
                        Name your tutorial and add any additional information
                        for internal purposes.
                      </p>
                    </div>
                  </div>
                  <div className="mb-[22px]">
                    <label
                      htmlFor="title"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Title
                      <Tooltip tooltip="Title">
                        <img src={infoIcon} alt="Info" className="ml-1" />
                      </Tooltip>
                    </label>
                    <Field
                      type="text"
                      id="title"
                      name="title"
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                  <div className="w-full mb-[22px]">
                    <label
                      htmlFor="tutorial_description"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Tutorial description
                    </label>
                    <Field
                      as="textarea"
                      id="tutorial_description"
                      name="tutorial_description"
                      rows={4}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                    <ErrorMessage
                      name="tutorial_description"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                  <div className="w-full mb-[22px]">
                    <label
                      htmlFor="link"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Link to video
                    </label>
                    <Field
                      type="text"
                      id="link"
                      name="link"
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                    <ErrorMessage
                      name="link"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="difficulty_level"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Difficulty level
                    </label>
                    <Field
                      as="select"
                      id="difficulty_level"
                      name="difficulty_level"
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      onChange={(e) => handleChange(e)}
                    >
                      {levelList?.map((d, index) => (
                        <option key={index} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="domain"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] w-full max-w-[375px] mt-[30px] lg:mt-[0] h-fit">
                <div className="flex items-center mb-[22px]">
                  <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                    <img
                      src={primaryIcon}
                      alt="Primary Icon"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <h2 className="text-[18px] text-black font-semibold flex items-center">
                      Preview Image
                      <Tooltip tooltip="Preview Image">
                        <img src={infoIcon} alt="Info" className="ml-1" />
                      </Tooltip>
                    </h2>
                  </div>
                </div>
                {!tutorialData.preview_url && (
                  <div
                    className="border-dashed rounded-[6px] border-2 border-gray-300 p-4 text-center flex flex-col items-center mb-[27px]"
                    onDrop={(e) => handleFileDrop(e)}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <img
                      src={cloudIcon}
                      alt="Cloud"
                      className="max-w-[31px] mb-2"
                    />
                    <p className="text-[13px] text-black font-bold">
                      Drag and Drop files here
                    </p>
                    <p className="text-[10px] text-[#8C8C8C]">or</p>

                    <input
                      type="file"
                      id="fileInput"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFileSelect(e)}
                    />

                    <label
                      htmlFor="fileInput"
                      className="inline-block mt-2 px-[8px] py-[3px] bg-gradient text-white text-[8px] font-semibold rounded-[5px] cursor-pointer"
                    >
                      Browse Files
                    </label>

                    <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                      Max file size: 5 MB per image
                    </p>
                  </div>
                )}

                <div>
                  <Fancybox
                    options={{
                      Carousel: {
                        infinite: false,
                      },
                    }}
                  >
                    {tutorialData.preview_url && (
                      <div className="flex flex-col justify-center w-[46%]">
                        <div className="videoPreview">
                          <img
                            src={tutorialData.preview_url}
                            alt="Preview"
                            className="max-w-[154px] max-h-[88px] w-full photo"
                          />
                          <div className="wrapper-icons flex justify-center gap-[40px]">
                            <div>
                              <a
                                data-fancybox="gallery"
                                href={tutorialData.preview_url}
                                className="flex items-center flex-col gap-[4px]"
                              >
                                <span className="text-white text-[7px]">
                                  View
                                </span>
                              </a>
                            </div>
                            <div>
                              <button
                                type="button"
                                className="flex items-center flex-col gap-[4px]"
                                onClick={() => handleDeletePrimaryImage()}
                              >
                                <img src={trashIcon} alt="trash Icon" />
                                <span className="text-white text-[7px]">
                                  Remove
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {uploadingPrimaryImage && (
                      <div className="absolute top-0 left-0 w-full h-full bg-gray-400 opacity-[0.5] flex items-center justify-center rounded-[4px]">
                        <img
                          src={loader}
                          alt="Loader"
                          className="w-[20px] h-[20px]"
                        />
                      </div>
                    )}
                  </Fancybox>
                </div>
              </div>
            </div>
          </div>

          <div className="sticky w-full bottom-0 left-0 flex justify-between bg-white border-t border-t-[#E0E0E0] py-[18px] px-[35px]">
            <div className="flex">
              <button
                type="submit"
                className={`flex bg-gradient text-white text-[10px] font-[600] ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""} rounded-[5px] px-[13px] py-[8px] mr-[13px]`}
                disabled={isSubmitting}
              >
                <img src={diskIcon} alt="Disk Icon" className="mr-[5px]" />
                Save Tutorial
              </button>
            </div>

            {isModalOpen && (
              <Modal
                message={modalMessage}
                isError={isError}
                onClose={() => setIsModalOpen(false)}
              />
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default EditTutorialForm;
